import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { getTasks, getFunctionalAreas, getPriorities, getStatuses } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  Filter, 
  Loader2, 
  ArrowRight,
  Calendar,
  X,
  HelpCircle,
  Eye,
  EyeOff,
  ArrowUpDown,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { TasksHelpDialog } from '@/components/HelpDialogs';

const priorityColors = {
  'Urgente': 'bg-red-100 text-red-700 border-red-200',
  'Alta': 'bg-orange-100 text-orange-700 border-orange-200',
  'Media-Alta': 'bg-amber-100 text-amber-700 border-amber-200',
  'Media': 'bg-blue-100 text-blue-700 border-blue-200',
  'Media-Baja': 'bg-slate-100 text-slate-700 border-slate-200',
  'Baja': 'bg-stone-100 text-stone-600 border-stone-200',
};

const statusColors = {
  'Pendiente': 'bg-amber-100 text-amber-700 border-amber-200',
  'En Proceso': 'bg-blue-100 text-blue-700 border-blue-200',
  'Completada': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Bloqueada': 'bg-red-100 text-red-700 border-red-200',
  'Cancelada': 'bg-slate-100 text-slate-500 border-slate-200',
};

const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const TasksPage = () => {
  const { canManageTasks, isSuperadmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filterArea, setFilterArea] = useState(searchParams.get('area') || 'all');
  const [filterPriority, setFilterPriority] = useState(searchParams.get('priority') || 'all');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  
  // Help dialog
  const [showHelp, setShowHelp] = useState(false);
  
  // Sort
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    loadTasks();
  }, [filterArea, filterPriority, filterStatus, sortBy, sortOrder]);

  const loadConfig = async () => {
    try {
      const [areasRes, prioritiesRes, statusesRes] = await Promise.all([
        getFunctionalAreas(),
        getPriorities(),
        getStatuses(),
      ]);
      setAreas(areasRes.data.areas);
      setPriorities(prioritiesRes.data.priorities);
      setStatuses(statusesRes.data.statuses);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterArea && filterArea !== 'all') params.functional_area = filterArea;
      if (filterPriority && filterPriority !== 'all') params.priority = filterPriority;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      params.sort_by = sortBy;
      params.sort_order = sortOrder;
      
      const response = await getTasks(params);
      setTasks(response.data);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Error al cargar las tareas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadTasks();
  };

  const clearFilters = () => {
    setSearch('');
    setFilterArea('all');
    setFilterPriority('all');
    setFilterStatus('all');
    setSearchParams({});
  };

  const hasFilters = search || (filterArea && filterArea !== 'all') || (filterPriority && filterPriority !== 'all') || (filterStatus && filterStatus !== 'all');

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="tasks-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
                Tareas
              </h1>
              {canManageTasks() && (
                <button
                  onClick={() => setShowHelp(true)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="tasks-help-btn"
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
              )}
            </div>
            <p className="text-slate-500 mt-1">{tasks.length} tareas encontradas</p>
          </div>
          {canManageTasks() && (
            <Link to="/tasks/new">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-task-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Tarea
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-6 bg-white border border-stone-200 shadow-sm">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Buscar por titulo, descripcion o numero..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-11 border-stone-200"
                  data-testid="search-input"
                />
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Select value={filterArea} onValueChange={setFilterArea}>
                  <SelectTrigger className="w-[180px] h-11 border-stone-200" data-testid="filter-area">
                    <Filter className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las areas</SelectItem>
                    {areas.map((area) => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-[160px] h-11 border-stone-200" data-testid="filter-priority">
                    <SelectValue placeholder="Prioridad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {priorities.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[160px] h-11 border-stone-200" data-testid="filter-status">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {statuses.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 h-11">
                  Buscar
                </Button>

                {hasFilters && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={clearFilters}
                    className="h-11 text-slate-600"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpiar
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Tasks List */}
        <Card className="bg-white border border-stone-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <p className="text-lg font-medium">No se encontraron tareas</p>
                <p className="text-sm">Intenta con otros filtros o crea una nueva tarea</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Numero</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Titulo</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Area</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Prioridad</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Estado</th>
                      {(isSuperadmin() || user?.role === 'ugib') && (
                        <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Visib.</th>
                      )}
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">
                        <button 
                          onClick={() => toggleSort('created_at')} 
                          className="flex items-center gap-1 hover:text-slate-700 transition-colors"
                          data-testid="sort-created"
                        >
                          Fecha
                          {sortBy === 'created_at' && <ArrowUpDown className={`w-3 h-3 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />}
                        </button>
                      </th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">
                        <button 
                          onClick={() => toggleSort('updated_at')} 
                          className="flex items-center gap-1 hover:text-slate-700 transition-colors"
                          data-testid="sort-updated"
                        >
                          Ult.Mod.
                          {sortBy === 'updated_at' && <ArrowUpDown className={`w-3 h-3 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />}
                        </button>
                      </th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {tasks.map((task) => (
                      <tr 
                        key={task.id} 
                        className="hover:bg-stone-50 transition-colors group"
                        data-testid={`task-row-${task.sequential_number}`}
                      >
                        <td className="p-4">
                          <span className="font-mono text-sm text-slate-600">{task.sequential_number}</span>
                        </td>
                        <td className="p-4">
                          <Link 
                            to={`/tasks/${task.id}`}
                            className="font-medium text-slate-900 hover:text-emerald-700 transition-colors"
                          >
                            {task.title}
                          </Link>
                          <p className="text-sm text-slate-500 truncate max-w-xs">{stripHtml(task.description)}</p>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <span className="text-sm text-slate-600">{task.functional_area}</span>
                        </td>
                        <td className="p-4">
                          <Badge className={`${priorityColors[task.priority]} text-[10px] font-bold uppercase border`}>
                            {task.priority}
                          </Badge>
                          {task.created_by_role === 'ugib' && (
                            <Badge className="ml-1 bg-violet-100 text-violet-700 border border-violet-200 text-[10px] font-bold uppercase">
                              UGIB
                            </Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <Badge className={`${statusColors[task.status]} text-[10px] font-bold uppercase border`}>
                            {task.status}
                          </Badge>
                        </td>
                        {(isSuperadmin() || user?.role === 'ugib') && (
                          <td className="p-4">
                            {task.visible_to_departamentos ? (
                              <Eye className="w-4 h-4 text-emerald-500" title="Visible para Departamentos" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-amber-500" title="Oculta para Departamentos" />
                            )}
                          </td>
                        )}
                        <td className="p-4 hidden lg:table-cell">
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(task.created_at), 'dd MMM yyyy', { locale: es })}
                          </div>
                        </td>
                        <td className="p-4 hidden lg:table-cell" data-testid={`task-updated-${task.sequential_number}`}>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Clock className="w-4 h-4" />
                            {task.updated_at 
                              ? format(new Date(task.updated_at), 'dd MMM yyyy', { locale: es })
                              : format(new Date(task.created_at), 'dd MMM yyyy', { locale: es })
                            }
                          </div>
                        </td>
                        <td className="p-4">
                          <Link to={`/tasks/${task.id}`}>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Help Dialog */}
      <TasksHelpDialog open={showHelp} onClose={() => setShowHelp(false)} />
    </Layout>
  );
};

export default TasksPage;
